import * as THREE from 'three';
import { snappingService } from '../../../../services/SnappingService';
import { visualizeBoundingBox, visualizeCollision } from './geometryUtils';

/**
 * Creates test objects for verifying snapping and edge detection
 * @param scene Three.js scene to add objects to
 * @returns Object containing test objects
 */
export const createTestObjects = (scene: THREE.Scene) => {
  // Create a red cube
  const cubeGeometry = new THREE.BoxGeometry(1, 1, 1);
  const cubeMaterial = new THREE.MeshBasicMaterial({ color: 0xff0000 });
  const redCube = new THREE.Mesh(cubeGeometry, cubeMaterial);
  redCube.position.set(-2, 0.5, 0);
  redCube.userData.draggable = true;
  redCube.userData.name = 'Red Cube';
  scene.add(redCube);

  // Create a blue cube
  const blueMaterial = new THREE.MeshBasicMaterial({ color: 0x0000ff });
  const blueCube = new THREE.Mesh(cubeGeometry, blueMaterial);
  blueCube.position.set(2, 0.5, 0);
  blueCube.userData.draggable = true;
  blueCube.userData.name = 'Blue Cube';
  scene.add(blueCube);

  // Create a green sphere
  const sphereGeometry = new THREE.SphereGeometry(0.5, 32, 32);
  const sphereMaterial = new THREE.MeshBasicMaterial({ color: 0x00ff00 });
  const greenSphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
  greenSphere.position.set(0, 0.5, 2);
  greenSphere.userData.draggable = true;
  greenSphere.userData.name = 'Green Sphere';
  scene.add(greenSphere);

  return { redCube, blueCube, greenSphere };
};

/**
 * Test function to verify the snapping functionality
 * @param scene Three.js scene containing objects
 * @param object Object to test snapping with
 */
export const testSnapping = (scene: THREE.Scene, object: THREE.Mesh) => {
  console.log('Testing snapping for', object.userData.name);

  // Test grid snapping
  const originalPosition = object.position.clone();
  const snappedToGrid = snappingService.snapToGrid(originalPosition);

  console.log('Original position:', originalPosition);
  console.log('Snapped to grid:', snappedToGrid);

  // Temporarily move object to a position that should snap to another object
  const targetObject = scene.children.find(
    (child) =>
      child instanceof THREE.Mesh &&
      child !== object &&
      child.userData.draggable
  ) as THREE.Mesh;

  if (targetObject) {
    // Save original positions
    const origObjectPos = object.position.clone();

    // Move object near the target
    object.position.set(
      targetObject.position.x + 0.1, // Slightly offset
      object.position.y,
      targetObject.position.z + 0.1
    );

    // Test edge snapping
    console.log('Testing edge snapping near', targetObject.userData.name);
    const snappedToEdge = snappingService.snapToEdge(object, scene);

    console.log(
      'Position before edge snap:',
      new THREE.Vector3(
        targetObject.position.x + 0.1,
        object.position.y,
        targetObject.position.z + 0.1
      )
    );
    console.log('Position after edge snap:', object.position);
    console.log('Snapped to edge:', snappedToEdge);

    // Visualize snap points
    snappingService.visualizeSnapPoints(scene, object);
    snappingService.visualizeSnapPoints(scene, targetObject);

    // Return to original position after 3 seconds
    setTimeout(() => {
      object.position.copy(origObjectPos);
    }, 3000);
  }
};

/**
 * Test function to verify the overlap detection functionality
 * @param scene Three.js scene containing objects
 * @param object Object to test overlap with
 */
export const testOverlap = (scene: THREE.Scene, object: THREE.Mesh) => {
  console.log('Testing overlap detection for', object.userData.name);

  // Save original position
  const originalPosition = object.position.clone();

  // Find a target object
  const targetObject = scene.children.find(
    (child) =>
      child instanceof THREE.Mesh &&
      child !== object &&
      child.userData.draggable
  ) as THREE.Mesh;

  if (targetObject) {
    // Move object to overlap with target
    object.position.copy(targetObject.position);

    // Check for overlap
    const overlapping = snappingService.checkOverlap(object, scene);

    console.log('Checking overlap with', targetObject.userData.name);
    console.log(
      'Overlapping objects:',
      overlapping.map((o) => o.userData.name)
    );

    // Visualize the collision
    if (overlapping.length > 0) {
      visualizeCollision(scene, object, overlapping[0]);
    }

    // Return to original position after 3 seconds
    setTimeout(() => {
      object.position.copy(originalPosition);
    }, 3000);
  }
};

/**
 * Test function to verify the out-of-bounds detection
 * @param scene Three.js scene containing objects
 * @param object Object to test bounds with
 * @param floorPlane Floor plane defining the room boundaries
 */
export const testOutOfBounds = (
  _scene: THREE.Scene,
  object: THREE.Mesh,
  floorPlane: THREE.Mesh
) => {
  console.log('Testing out-of-bounds detection for', object.userData.name);

  // Save original position
  const originalPosition = object.position.clone();

  // Get floor bounds
  const floorBounds = new THREE.Box3().setFromObject(floorPlane);

  // Move object outside the floor bounds
  object.position.set(
    floorBounds.max.x + 2, // Definitely outside
    object.position.y,
    object.position.z
  );

  // Check if out of bounds
  const isOutOfBounds = snappingService.checkOutOfBounds(object, floorPlane);

  console.log('Object position:', object.position);
  console.log('Floor bounds:', floorBounds);
  console.log('Is out of bounds:', isOutOfBounds);

  // Return to original position after 3 seconds
  setTimeout(() => {
    object.position.copy(originalPosition);
  }, 3000);
};

/**
 * Creates test objects and demonstrates edge detection capabilities
 * @param scene Three.js scene
 * @param floorPlane Floor plane for boundary detection
 */
export const testEdgeDetection = (
  scene: THREE.Scene,
  floorPlane: THREE.Mesh
): void => {
  // Create test objects
  const testObjects: THREE.Mesh[] = [];

  // Create cube objects with different rotations
  for (let i = 0; i < 3; i++) {
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshStandardMaterial({
      color: Math.random() * 0xffffff,
      transparent: true,
      opacity: 0.7,
    });

    const cube = new THREE.Mesh(geometry, material);
    cube.position.set(-2 + i * 2, 0.5, -2);

    // Apply different rotations to test the enhanced edge detection
    cube.rotation.y = (i * Math.PI) / 4; // 0, 45, 90 degrees

    // Set up for dragging
    cube.userData.draggable = true;
    cube.userData.type = 'testCube';

    scene.add(cube);
    testObjects.push(cube);
  }

  // Create an irregularly shaped object
  const irregularShape = new THREE.Mesh(
    new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(-0.5, 0, -0.5),
      new THREE.Vector3(0.5, 0, -0.5),
      new THREE.Vector3(1, 0, 0.5),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(-1, 0, 0.5),
    ]),
    new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      transparent: true,
      opacity: 0.7,
      side: THREE.DoubleSide,
    })
  );
  irregularShape.position.set(3, 0.01, 3);
  irregularShape.userData.draggable = true;
  irregularShape.userData.type = 'irregular';
  scene.add(irregularShape);
  testObjects.push(irregularShape);

  // Visualize bounding boxes for all objects
  testObjects.forEach((obj, index) => {
    // Use different colors for each object's bounding box
    const colors = [0xff0000, 0x00ff00, 0x0000ff, 0xffff00];
    visualizeBoundingBox(scene, obj, colors[index % colors.length]);
  });

  // Test collision detection between objects
  console.log('Testing collision detection:');
  for (let i = 0; i < testObjects.length; i++) {
    for (let j = i + 1; j < testObjects.length; j++) {
      const overlaps = snappingService.checkOverlap(
        testObjects[i],
        scene,
        null,
        { objectTypes: ['testCube', 'irregular'] }
      );

      if (overlaps.includes(testObjects[j])) {
        console.log(`Object ${i} overlaps with Object ${j}`);
      }
    }
  }

  // Test boundary detection
  console.log('Testing boundary detection:');
  testObjects.forEach((obj, index) => {
    const isOutOfBounds = snappingService.checkOutOfBounds(obj, floorPlane, {
      tolerance: 0.25, // Allow 25% of corners to be outside
    });
    console.log(`Object ${index} out of bounds: ${isOutOfBounds}`);
  });

  // Visualize room boundaries
  snappingService.visualizeBoundary(scene, floorPlane, {
    showWarningZone: true,
    warningZoneOffset: 1.0,
  });

  // Create a rotated object close to boundary for testing
  const boundaryTestCube = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshStandardMaterial({
      color: 0xff00ff,
      transparent: true,
      opacity: 0.7,
    })
  );

  // Get floor bounds to position the test cube
  const floorBounds = new THREE.Box3().setFromObject(floorPlane);
  boundaryTestCube.position.set(
    floorBounds.max.x - 0.75, // Near edge
    0.5,
    floorBounds.max.z - 0.75
  );
  boundaryTestCube.rotation.y = Math.PI / 4; // 45 degrees
  boundaryTestCube.userData.draggable = true;
  boundaryTestCube.userData.type = 'boundaryTest';

  scene.add(boundaryTestCube);
  visualizeBoundingBox(scene, boundaryTestCube, 0xffff00);

  const isOutOfBounds = snappingService.checkOutOfBounds(
    boundaryTestCube,
    floorPlane,
    { tolerance: 0.25 }
  );
  console.log(`Boundary test cube out of bounds: ${isOutOfBounds}`);
};

/**
 * Creates test objects and demonstrates overlap detection
 * @param scene Three.js scene
 * @param floorPlane Floor plane for boundary detection
 */
export const testOverlapDetection = (
  scene: THREE.Scene,
  floorPlane: THREE.Mesh
): void => {
  // Clean up any existing test objects
  scene.children.forEach((child) => {
    if (child.userData.isTestObject) {
      scene.remove(child);
    }
  });

  // Create test objects
  const object1 = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshStandardMaterial({
      color: 0xff0000,
      transparent: true,
      opacity: 0.7,
    })
  );
  object1.position.set(0, 0.5, 0);
  object1.userData.draggable = true;
  object1.userData.isTestObject = true;
  scene.add(object1);

  const object2 = new THREE.Mesh(
    new THREE.BoxGeometry(1, 1, 1),
    new THREE.MeshStandardMaterial({
      color: 0x00ff00,
      transparent: true,
      opacity: 0.7,
    })
  );
  object2.position.set(0.5, 0.5, 0.5);
  object2.userData.draggable = true;
  object2.userData.isTestObject = true;
  scene.add(object2);

  // Check for overlaps
  const overlappingObjects = snappingService.checkOverlap(object1, scene);
  console.log('Overlapping objects:', overlappingObjects);

  // Visualize the collision
  if (overlappingObjects.length > 0) {
    visualizeCollision(scene, object1, overlappingObjects[0]);
  }

  // Test out of bounds
  const isOutOfBounds = snappingService.checkOutOfBounds(object1, floorPlane);
  console.log('Is out of bounds:', isOutOfBounds);

  // Clean up after a few seconds
  setTimeout(() => {
    scene.children.forEach((child) => {
      if (
        child.userData.isTestObject ||
        child.userData.isCollisionVisualization
      ) {
        scene.remove(child);
      }
    });
  }, 3000);
};
